import React, { useState, useEffect, useMemo } from 'react';
import { useStores, invalidateStore } from '../hooks/useDB';
import PageLoader from '../components/PageLoader';
import {
  Activity, Calendar, MapPin, Plane, Clock, Phone, Receipt,
  CheckCircle2, ShieldAlert, BedDouble, Wallet, Image as ImageIcon,
  ChevronRight, Ticket, UtensilsCrossed, User, RefreshCw,
} from 'lucide-react';
import { money } from '../utils/formatters';

const EVENT_FALLBACK = new Date(2026, 8, 5, 0, 0, 0); // Sep 5 2026

function fmtDate(d) {
  if (!d) return 'TBD';
  try {
    return new Date(d + 'T12:00:00').toLocaleDateString('en-IN', { day: 'numeric', month: 'short', year: 'numeric' });
  } catch { return d; }
}

function greeting() {
  const h = new Date().getHours();
  if (h < 12) return 'Good morning';
  if (h < 17) return 'Good afternoon';
  return 'Good evening';
}

const navTo = (id) => window.dispatchEvent(new CustomEvent('planner:navigate', { detail: id }));

const EMP_STORES = ['events', 'venues', 'schedule', 'budget', 'attendees'];

export default function EmployeeDashboard({ currentUser = {} }) {
  const [lastUpdated, setLastUpdated] = useState('');

  const { events, venues, schedule, budget, attendees, loading } = useStores(EMP_STORES);

  // Derive current user's attendee record from cache
  const me = useMemo(
    () => attendees.find(a => (a.email || '').toLowerCase() === (currentUser.email || '').toLowerCase()) || null,
    [attendees, currentUser.email]
  );

  useEffect(() => {
    if (!loading) setLastUpdated(new Date().toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' }));
  }, [loading]);

  const firstName = (currentUser.name || 'there').split(' ')[0];

  // ── Event + countdown ──────────────────────────────────────────────────────
  const upcomingEvent = [...events]
    .filter(e => e.date)
    .sort((a, b) => new Date(a.date) - new Date(b.date))[0] || null;
  const eventDate = upcomingEvent?.date ? new Date(upcomingEvent.date + 'T00:00:00') : EVENT_FALLBACK;
  const daysToGo = Math.max(0, Math.ceil((eventDate.getTime() - Date.now()) / 86400000));

  // ── My status ──────────────────────────────────────────────────────────────
  const rsvp = me?.rsvp || 'pending';
  const travelStatus = me?.travelStatus || 'pending';
  const travelBooked = ['booked', 'issued', 'completed'].includes(travelStatus);

  // ── My reimbursements ────────────────────────────────────────────────────────
  const myInvoices = budget.filter(b => b.reimbursement && (b.submittedByEmail || '').toLowerCase() === (currentUser.email || '').toLowerCase());
  const totalSubmitted = myInvoices.reduce((s, b) => s + (parseFloat(b.amount) || 0), 0);
  const paidOut = myInvoices.filter(b => b.status === 'paid').reduce((s, b) => s + (parseFloat(b.amount) || 0), 0);
  const pendingInvoices = myInvoices.filter(b => !['paid', 'cancelled'].includes(b.status)).length;

  // ── Itinerary (published, sorted) ────────────────────────────────────────────
  const upcomingSessions = schedule
    .filter(s => s.published)
    .sort((a, b) => {
      const da = `${a.date || ''} ${a.startTime || ''}`;
      const db = `${b.date || ''} ${b.startTime || ''}`;
      return da.localeCompare(db);
    })
    .slice(0, 5);

  // ── Key POCs ─────────────────────────────────────────────────────────────────
  const contacts = [];
  events.forEach(e => { if (e.primaryPocName) contacts.push({ source: 'Event', context: e.name, name: e.primaryPocName, phone: e.primaryPocPhone }); });
  venues.forEach(v => { if (v.contactName) contacts.push({ source: v.venueType || 'Venue', context: v.name, name: v.contactName, phone: v.contactPhone }); });
  schedule.forEach(s => { if (s.pocName && !contacts.some(c => c.name === s.pocName)) contacts.push({ source: 'Session', context: s.title, name: s.pocName, phone: s.pocPhone }); });
  const topContacts = contacts.slice(0, 4);

  const typeIcon = { meal: UtensilsCrossed, session: Calendar, activity: Activity };

  if (loading) return <PageLoader label="Loading your dashboard…" sublabel="Pulling together your trip details" />;

  return (
    <div style={{ animation: 'fade-in 0.3s ease-out' }}>

      {/* PAGE HEADER */}
      <div className="page-header" style={{ flexWrap: 'wrap', gap: 12 }}>
        <div style={{ minWidth: 0 }}>
          <div className="page-title" style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
            <Activity size={28} style={{ color: 'var(--accent)', flexShrink: 0 }} />
            <span>{greeting()}, {firstName}!</span>
          </div>
          <div className="page-subtitle">
            Your personal offsite hub — trip, itinerary, contacts and reimbursements in one place
          </div>
        </div>
        <div style={{ display: 'flex', alignItems: 'center', gap: 12, flexShrink: 0 }}>
          {lastUpdated && (
            <div style={{ fontSize: 12, color: 'var(--text2)', fontFamily: 'var(--font-mono)' }}>Last sync: {lastUpdated}</div>
          )}
          <button className="btn btn-secondary" onClick={() => invalidateStore()} disabled={loading} style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
            <RefreshCw size={14} className={loading ? 'animate-spin' : ''} />
            <span>Refresh</span>
          </button>
        </div>
      </div>

      {/* STATS GRID */}
      <div className="sh-stat-grid">
        <div className="sh-stat-card">
          <div className="sh-stat-head">
            <span className="sh-stat-label">Days to Offsite</span>
            <Calendar size={18} style={{ color: 'var(--accent)' }} />
          </div>
          <div className="sh-stat-value">{daysToGo}</div>
          <div className="sh-stat-desc">{upcomingEvent?.name ? `${upcomingEvent.name} · ${fmtDate(upcomingEvent.date)}` : `Starts ${fmtDate('2026-09-05')}`}</div>
        </div>

        <div className="sh-stat-card">
          <div className="sh-stat-head">
            <span className="sh-stat-label">My RSVP</span>
            <CheckCircle2 size={18} style={{ color: rsvp === 'confirmed' ? 'var(--success)' : 'var(--warning)' }} />
          </div>
          <div className="sh-stat-value" style={{ textTransform: 'capitalize' }}>{rsvp}</div>
          <div className="sh-stat-desc">{rsvp === 'confirmed' ? "You're all set to attend" : 'Awaiting your confirmation'}</div>
        </div>

        <div className="sh-stat-card">
          <div className="sh-stat-head">
            <span className="sh-stat-label">My Travel</span>
            <Plane size={18} style={{ color: travelBooked ? 'var(--success)' : 'var(--warning)' }} />
          </div>
          <div className="sh-stat-value" style={{ textTransform: 'capitalize' }}>{travelStatus}</div>
          <div className="sh-stat-desc">{me?.travelMode ? `Mode: ${me.travelMode}` : 'No travel plan recorded yet'}</div>
        </div>

        <div className="sh-stat-card">
          <div className="sh-stat-head">
            <span className="sh-stat-label">My Reimbursements</span>
            <Wallet size={18} style={{ color: '#166534' }} />
          </div>
          <div className="sh-stat-value" style={{ fontSize: 24 }}>{money(totalSubmitted)}</div>
          <div className="sh-stat-desc">{pendingInvoices} pending · {money(paidOut)} paid out</div>
        </div>
      </div>

      {/* MAIN GRID */}
      <div className="grid-3" style={{ gap: 16 }}>

        {/* LEFT — My Trip + Itinerary (span 2) */}
        <div style={{ gridColumn: 'span 2', minWidth: 0, display: 'flex', flexDirection: 'column', gap: 16 }}>

          {/* My Trip */}
          <div className="sh-card">
            <div className="sh-card-header" style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
              <div>
                <div className="sh-card-title">My Trip Details</div>
                <div className="sh-card-description">Your travel, accommodation and event summary</div>
              </div>
              <span className={`sh-badge ${rsvp === 'confirmed' ? 'sh-badge-success' : 'sh-badge-warning'}`} style={{ textTransform: 'capitalize' }}>{rsvp}</span>
            </div>

            {!me ? (
              <div style={{ background: '#fffbeb', border: '1px solid #fde68a', borderRadius: 10, padding: 16, display: 'flex', gap: 12 }}>
                <ShieldAlert size={18} style={{ color: 'var(--warning)', flexShrink: 0, marginTop: 2 }} />
                <div style={{ fontSize: 13, color: '#92400e' }}>
                  <strong style={{ display: 'block', marginBottom: 2 }}>Complete your profile</strong>
                  We couldn't find your travel record yet. Add your details so we can plan your travel and accommodation.
                  <button className="btn btn-sm btn-secondary" style={{ marginTop: 10 }} onClick={() => navTo('profile')}>
                    <User size={13} /> Go to Profile
                  </button>
                </div>
              </div>
            ) : (
              <>
                {/* Event summary strip */}
                {upcomingEvent && (
                  <div style={{ border: '1px solid #f4f4f5', borderRadius: 8, padding: 14, background: 'linear-gradient(to right, #ffffff, #fafafa)', marginBottom: 14 }}>
                    <strong style={{ fontSize: 15, color: '#09090b' }}>{upcomingEvent.name}</strong>
                    <div style={{ fontSize: 13, color: '#71717a', marginTop: 4, display: 'flex', flexWrap: 'wrap', gap: 12 }}>
                      <span style={{ display: 'inline-flex', alignItems: 'center', gap: 4 }}><Calendar size={13} /> {fmtDate(upcomingEvent.date)}{upcomingEvent.endDate ? ` – ${fmtDate(upcomingEvent.endDate)}` : ''}</span>
                      <span style={{ display: 'inline-flex', alignItems: 'center', gap: 4 }}><MapPin size={13} /> {upcomingEvent.venue || 'TBD'}</span>
                    </div>
                  </div>
                )}

                {/* Detail tiles */}
                <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(150px, 1fr))', gap: 10 }}>
                  <TripTile icon={Plane} label="Travel Mode" value={me.travelMode || 'Not set'} sub={`Status: ${travelStatus}`} ok={travelBooked} />
                  <TripTile icon={Ticket} label="Booking Ref"
                    value={me.flightPnr ? `PNR ${me.flightPnr}` : me.cabBookingRef ? `Cab ${me.cabBookingRef}` : 'Pending'}
                    sub={me.flightNo || me.cabVendor || 'No reference yet'} ok={!!(me.flightPnr || me.cabBookingRef)} />
                  <TripTile icon={MapPin} label="Route"
                    value={me.fromCity ? `${me.fromCity} → ${me.toCity || 'Venue'}` : 'Not set'}
                    sub={me.departureDate ? `Dep ${fmtDate(me.departureDate)}` : 'Dates TBD'} ok={!!me.fromCity} />
                  <TripTile icon={BedDouble} label="Accommodation"
                    value={me.roomNeeded ? 'Room booked' : 'No room needed'}
                    sub={me.returnDate ? `Return ${fmtDate(me.returnDate)}` : 'Hotel stay'} ok={me.roomNeeded} />
                </div>
              </>
            )}
          </div>

          {/* Itinerary */}
          <div className="sh-card">
            <div className="sh-card-header" style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
              <div>
                <div className="sh-card-title">Event Itinerary</div>
                <div className="sh-card-description">Published sessions, meals and activities</div>
              </div>
              <span className="sh-badge sh-badge-outline">{schedule.filter(s => s.published).length} items</span>
            </div>

            {upcomingSessions.length === 0 ? (
              <div className="empty-state">
                <Clock className="empty-icon" size={28} />
                <h3>Itinerary not published yet</h3>
                <p>Your schedule will appear here once the organizers publish it.</p>
              </div>
            ) : (
              <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
                {upcomingSessions.map(s => {
                  const Icon = typeIcon[s.type] || Calendar;
                  return (
                    <div key={s.id} className="sh-list-item" style={{ border: '1px solid #f4f4f5', borderRadius: 8, padding: 12, gap: 12 }}>
                      <div style={{ width: 38, height: 38, borderRadius: 8, background: 'rgba(249,101,22,0.1)', color: 'var(--accent)', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
                        <Icon size={17} />
                      </div>
                      <div style={{ minWidth: 0, flex: 1 }}>
                        <strong style={{ fontSize: 13, color: '#09090b' }}>{s.title}</strong>
                        <div style={{ fontSize: 11, color: 'var(--text2)', marginTop: 2, display: 'flex', flexWrap: 'wrap', gap: 8 }}>
                          <span>{s.day || fmtDate(s.date)}</span>
                          <span style={{ display: 'inline-flex', alignItems: 'center', gap: 3 }}><MapPin size={11} /> {s.location || s.conferenceRoom || 'TBD'}</span>
                        </div>
                      </div>
                      <span style={{ fontFamily: 'var(--font-mono)', fontWeight: 700, color: 'var(--accent)', fontSize: 13, flexShrink: 0 }}>
                        {s.startTime || '--:--'}
                      </span>
                    </div>
                  );
                })}
                <button className="btn btn-sm btn-secondary" style={{ alignSelf: 'flex-start', marginTop: 4 }} onClick={() => navTo('schedule')}>
                  <Clock size={13} /> View full itinerary
                </button>
              </div>
            )}
          </div>
        </div>

        {/* RIGHT — POCs + Quick actions (span 1) */}
        <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>

          {/* Who to call */}
          <div className="sh-card">
            <div className="sh-card-header">
              <div className="sh-card-title">Who to Call</div>
              <div className="sh-card-description">Your on-ground points of contact</div>
            </div>

            {topContacts.length === 0 ? (
              <div className="empty-state">
                <Phone className="empty-icon" size={28} />
                <h3>No contacts yet</h3>
                <p>POCs appear once organizers add them.</p>
              </div>
            ) : (
              <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
                {topContacts.map((c, i) => (
                  <div key={i} className="sh-list-item" style={{ borderRadius: 8, border: '1px solid #f4f4f5', gap: 8 }}>
                    <div style={{ minWidth: 0, flex: 1 }}>
                      <span className="sh-badge sh-badge-outline" style={{ fontSize: 9, textTransform: 'uppercase' }}>{c.source}</span>
                      <strong style={{ display: 'block', fontSize: 13, marginTop: 4 }}>{c.name}</strong>
                      <div style={{ fontSize: 11, color: 'var(--text2)' }}>{c.context}</div>
                    </div>
                    {c.phone && (
                      <a href={`tel:${c.phone}`} title={`Call ${c.name}`}
                        style={{ display: 'inline-flex', alignItems: 'center', justifyContent: 'center', width: 32, height: 32, borderRadius: '50%', background: 'var(--success)', color: '#fff', flexShrink: 0 }}>
                        <Phone size={14} />
                      </a>
                    )}
                  </div>
                ))}
                <button className="btn btn-sm btn-secondary" style={{ alignSelf: 'flex-start', marginTop: 4 }} onClick={() => navTo('contacts')}>
                  <Phone size={13} /> All contacts
                </button>
              </div>
            )}
          </div>

          {/* Quick actions */}
          <div className="sh-card">
            <div className="sh-card-header">
              <div className="sh-card-title">Quick Actions</div>
              <div className="sh-card-description">Jump to what you need</div>
            </div>
            <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
              <QuickAction icon={Receipt} label="Submit an expense" sub={`${pendingInvoices} pending review`} onClick={() => navTo('invoices')} />
              <QuickAction icon={ImageIcon} label="Event gallery" sub="Relive the moments" onClick={() => navTo('gallery')} />
              <QuickAction icon={Calendar} label="My events" sub="What's planned" onClick={() => navTo('events')} />
              <QuickAction icon={User} label="My profile" sub="Update your details" onClick={() => navTo('profile')} />
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

function TripTile({ icon: Icon, label, value, sub, ok }) {
  return (
    <div style={{ border: '1px solid #f4f4f5', borderRadius: 8, padding: 12, background: '#ffffff' }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: 6, marginBottom: 6 }}>
        <Icon size={14} style={{ color: ok ? 'var(--success)' : 'var(--accent)' }} />
        <span style={{ fontSize: 10, fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.05em', color: 'var(--text2)' }}>{label}</span>
      </div>
      <div style={{ fontSize: 14, fontWeight: 700, color: '#09090b', textTransform: 'capitalize' }}>{value}</div>
      <div style={{ fontSize: 11, color: 'var(--text3)', marginTop: 2 }}>{sub}</div>
    </div>
  );
}

function QuickAction({ icon: Icon, label, sub, onClick }) {
  return (
    <button onClick={onClick} className="sh-list-item" style={{ width: '100%', textAlign: 'left', border: '1px solid #f4f4f5', borderRadius: 8, cursor: 'pointer', background: '#fff', gap: 12 }}>
      <div style={{ width: 36, height: 36, borderRadius: 8, background: 'rgba(249,101,22,0.1)', color: 'var(--accent)', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
        <Icon size={16} />
      </div>
      <div style={{ minWidth: 0, flex: 1 }}>
        <strong style={{ fontSize: 13, color: '#09090b', display: 'block' }}>{label}</strong>
        <span style={{ fontSize: 11, color: 'var(--text2)' }}>{sub}</span>
      </div>
      <ChevronRight size={16} style={{ color: 'var(--text3)', flexShrink: 0 }} />
    </button>
  );
}
